// © Microsoft Corporation. All rights reserved.

import {
    evalGitOrCmsExperienceInfo,
    getConfigHref,
    getConfigId,
    getConfigIdString,
    getExperienceType,
    resolver,
    ChildExperienceUniversalConfigInfo,
    DynamicBundles,
    ExperienceConfigWrapper
} from "@msnews/core";
import { ChildExperienceConfigInfo, IRequestContext } from "@msnews/core";
import { ConnectComponentFailureError, createError, ExperienceLoaderAppErrors, LoadComponentFailureError, renamePerfTimeline, stringifyError, tryGetPerfTimeline } from "@msnews/diagnostics";
import { ExperienceLoadingState, MilestoneDataActions, createExperienceStateUpdate, getExperienceLoadDelegate, getMilestoneDataConnector } from "@msnews/milestone-data";

import { ConfigProcessor } from "@msnews/config-validator";
import { ExperienceIds } from "@msnews/experiences-constants";
import { IComponentConnector } from "../";
import { PageBase, reportDataConnectorStateChange } from "@msnews/experiences-redux";
import React from "react";
import { Store } from "react-redux";
import { Telemetry } from "@msnews/telemetry-contracts";
import { uniqueId } from "lodash-es";
import { canUseDOM } from "@msnews/isomorphic-util";

/**
 * An experience loader class that encapsulates the dynamic loading of an experience.
 * Give an experience config href, this class will determine the bundle to be loaded, load the bundle, and then enable the caller to get
 * the connected render component for the experience.
 * The loading/initialization of the experience connector and the connecting are separate methods, so that the caller can control when each is done
 */
export class ExperienceLoader {
    /**
     * The connector for this instance of the experience loader. It is undefined until after loadConnector is called
     */
    private experienceConnector: IComponentConnector<any, any, any> = undefined;

    /**
     * The experience rendering Component for this instance of the experience loader. It is undefined until after loadConnector is called
     */
    private experienceComponent: any = undefined;

    /**
     * The experience Type of the experience being loaded. It is undefined until after loadConnector is called
     */
    private $experienceType: string;

    /**
     * The unique ID that identifes this loading experience instance.
     */
    private readonly instanceId?: string;

    /**
     * Gets the experience Type for this instance of the experience loader. It is undefined until after loadConnector is called
     */
    public get experienceType(): string { return this.$experienceType; }

    /**
     * Gets the current Connector for this experience loader instance
     */
    public get connector(): IComponentConnector<any, any, any> { return this.experienceConnector; }

    /**
     * When we want to load bundles preemptively with earlyLoadBundles, we'll store their promises here. Mapping from experienceType
     * string to the promise for loading its bundle.
     */
    public static earlyLoadBundlePromises: Map<string, Promise<any>> = new Map<string, Promise<any>>();

    /**
     * Creates an experience loader.
     * @param {ChildExperienceUniversalConfigInfo} configIndexId The config index ID for the config to load
     * @param {string} instanceId The instanceId of the experience
     * @param {string} parentNamespace The namespace of the parent experience, if it is known
     * @param {Store<any>} parentStore The parent experience redux store
     * @param {string} type The experienceType of the experience being loaded. If provided, this type will be used to load the bundle
     */
    public constructor(
        private readonly configIndexId: ChildExperienceUniversalConfigInfo,
        instanceId?: string | undefined,
        private readonly parentNamespace?: string | undefined,
        private readonly parentStore?: Store<any> | undefined,
        private readonly requestContext?: IRequestContext | undefined,
        experienceType?: string | undefined) {

        this.$experienceType = experienceType || ((typeof configIndexId !== "string") && getExperienceType(configIndexId));
        this.instanceId = instanceId || evalGitOrCmsExperienceInfo(
            this.configIndexId,
            gitConfigInfo => gitConfigInfo.instanceId,
            cmsConfigInfo => cmsConfigInfo.instanceId,
            _ => instanceId,
            ""
        );
    }
    /**
     * Given a child config, recursively find bundles that we can load immediately. If we find a bundle to load immediately, we'll
     * create a promise and store it in earlyLoadBundlePromises. The loadConnector method should check the earlyLoadBundlePromises
     * before creating new promises to a load bundle.
     * @param config child config of an experience config
     */
    public static async earlyLoadBundles(config: ChildExperienceConfigInfo) {

        const experienceType = getExperienceType(config);

        if (config
            && experienceType
            && !getExperienceLoadDelegate(experienceType)) {
            // Do not early load experiences with delegates. They should have specific events triggering loading.

            if (experienceType === ExperienceIds.river) {
                // We should not traverse to the River when early loading bundles because it may have children disabled.
                return;
            }

            if (!ExperienceLoader.earlyLoadBundlePromises.has(experienceType)) {
                ExperienceLoader.earlyLoadBundlePromises.set(experienceType, DynamicBundles.loadExperience(experienceType));
            }

            const configId = getConfigId(config);
            const fullConfig = await resolver.getConfig(configId);

            ConfigProcessor.getChildrenDataConnectorConfigInfos(fullConfig.properties).forEach(async (childConfig) => {
                ExperienceLoader.earlyLoadBundles(childConfig);
            });

            ConfigProcessor.getChildrenExperienceConfigInfos(fullConfig.properties).forEach(async (childConfig) => {
                ExperienceLoader.earlyLoadBundles(childConfig);
            });
        }
    }

    /**
     * Dynamically loads and experience bundle, and creates the experience connector
     */
    public async loadConnector(isDataConnector = false): Promise<void> {
        let linesRunUntilFailure = "start;";
        try {
            // Log an app error, if we didn't get either a configIndexHref NOR an experienceType
            if (!this.configIndexId && !this.$experienceType) {
                // Log an app error if the loader failed to load for whatever reason
                Telemetry.sendAppErrorEvent({
                    ...ExperienceLoaderAppErrors.InsufficientLoadArgs,
                    message: "Failed loading experience bundle due to missing configIndexId and experienceType",
                    pb: {
                        ...ExperienceLoaderAppErrors.InsufficientLoadArgs.pb,
                        customMessage: `Details: " ${JSON.stringify({ instanceId: this.instanceId, parentNamespace: this.parentNamespace })}`
                    }

                });
                throw new Error("Cannot load a connector bundle without either the experienceType, or configIndexId");
            }
            linesRunUntilFailure += "nullCheck;";

            const perfTimeline = tryGetPerfTimeline(this.$experienceType || uniqueId(), this.instanceId);
            linesRunUntilFailure += "perfTimeline;";

            // the loaded config result
            let configResult: ExperienceConfigWrapper;

            // Get a promise for the resolved config of the experience that is being initialized. If not configIndexHref is supplied,
            // then assume the experience has no config, so just create a resolved promise the returns an empty config
            let configPromise: any;
            const configId = this.configIndexId && getConfigId(this.configIndexId);
            if (configId) {
                configPromise = perfTimeline
                    ? perfTimeline.instrumentPromise("config", () => resolver.getConfig(configId))
                    : resolver.getConfig(configId);
            } else {
                configPromise = Promise.resolve(<ExperienceConfigWrapper>{
                    experienceType: this.$experienceType,
                    properties: {}
                });
            }

            linesRunUntilFailure += "configPromise;";

            // The async promises for loading the config and loading the bundles
            const loaderPromises: Promise<any>[] = [configPromise];

            // If we don't know the experienceType, then we need to wait for the config to load and get it from the child config
            if (!this.$experienceType) {

                configResult = await configPromise;

                // Note that Git configs do not return experience type with the config yet,
                // however, the experience type will be derived from the config id in the ctor.
                this.$experienceType = configResult.experienceType;

                linesRunUntilFailure += "awaitConfigPromise;";

                // if we didn't have $experienceType at construction time, then we would've used uniqueId() to create this
                // temporary timeline. Now that we have the proper type, rename the timeline to the known value.
                if (perfTimeline) {
                    renamePerfTimeline(perfTimeline, this.$experienceType);
                    linesRunUntilFailure += "renamePerfTimeline;";
                }
            }

            let bundlePromise: Promise<any>;
            if (!ExperienceLoader.earlyLoadBundlePromises.has(this.$experienceType)) {

                // Get a promise for loading in the experience bundle
                bundlePromise = perfTimeline
                    ? perfTimeline.instrumentPromise("scripts", () => DynamicBundles.loadExperience(this.$experienceType))
                    : DynamicBundles.loadExperience(this.$experienceType);

                loaderPromises.push(bundlePromise);
                linesRunUntilFailure += "loadExperienceBundle;";
            }

            // Wait for all of the promises to complete. This will either be both the config and bundle promises,
            // or the bundle promise with the pre-resolved config promise to the empty config object.
            await Promise.all(loaderPromises);
            linesRunUntilFailure += "waitForLoaderPromises;";

            // If we don't have the config result yet, get it from the completed promises.
            if (!configResult) {
                configResult = await configPromise;
                linesRunUntilFailure += "awaitConfigPromise;";
            }

            // Get the bundle from the completed promises
            const bundleModule = ExperienceLoader.earlyLoadBundlePromises.has(this.$experienceType)
                ? await ExperienceLoader.earlyLoadBundlePromises.get(this.$experienceType)
                : await bundlePromise;
            linesRunUntilFailure += "getBundleModule;";

            const initMeasureEnd = perfTimeline && perfTimeline.startMeasure("init").endMeasure;

            // update experience current state to loaded
            this.updateExperienceLoadedMilestone(this.$experienceType);
            linesRunUntilFailure += "updateExperienceLoadedMilestone;";

            // Extract config object from result
            const config = configResult.properties;

            // Get the Connector/Reducer/Component via naming convention
            const ExperienceConnector = bundleModule[this.$experienceType + "Connector"];
            const ExperienceReducer = bundleModule[this.$experienceType + "Reducer"];

            // Store the experience rendering component as a class member, for use later when it is connected
            this.experienceComponent = bundleModule[this.$experienceType];

            // Create the experience connector. The namespace is a combination of the experienceType and its instance ID (in case there are multiple of them on a page)
            const app = PageBase.getInstance();

            let experienceNamespace: string = null;
            if (this.configIndexId == null) {
                // configRef or HREF is not specified
                experienceNamespace = getExperienceNamespace({ instanceId: this.instanceId, configRef: { experienceType: this.$experienceType, instanceSrc: "NA" } });
            } else {
                experienceNamespace = typeof this.configIndexId === "string"
                    ? getExperienceNamespace({ experienceType: this.$experienceType, instanceId: this.instanceId, href: this.configIndexId })
                    : getExperienceNamespace(this.configIndexId, this.instanceId);
            }

            linesRunUntilFailure += "getExperienceNamespace;";

            const experienceParentNamespace = this.parentNamespace ? this.parentNamespace : "";
            const experienceStore = this.parentStore ? this.parentStore : app.store;

            // Data connectors are singletons, and so their registered namespace is only their experienceType. This allows
            // data connectors to be found by using the constants in the ExperienceIds enum
            this.experienceConnector = new ExperienceConnector(
                isDataConnector ? this.$experienceType : experienceNamespace,
                experienceParentNamespace,
                app.rootReducer,
                new ExperienceReducer(config),
                experienceStore,
                config,
                this.requestContext
            );
            linesRunUntilFailure += "newExperienceConnector;";

            // Report initial data connector state when loaded.
            if (isDataConnector && canUseDOM()) {
                reportDataConnectorStateChange(this.$experienceType, this.experienceConnector.getCurrentState());
            }

            // Set the experience config index href property on the connector.
            this.experienceConnector.configHref = getConfigHref(this.configIndexId);

            if (initMeasureEnd) {
                initMeasureEnd();
            }

        } catch (error) {

            Telemetry.sendAppErrorEvent(createError(
                LoadComponentFailureError,
                "loadComponent failed",
                `Details: ${JSON.stringify({
                    error: stringifyError(error, true),
                    instanceId: this.instanceId,
                    parentNamespace: this.parentNamespace,
                    configIndex: this.configIndexId,
                    linesRunUntilFailure
                })}`
            ));

            throw error;
        }
    }

    /**
     * Creates and returns the connected rendering component for the experience
     * @param connectorProps Any initial props that should be passed to the connector in its onComponentConnect() method
     * @returns The Connected component for the experience
     */
    public async getConnectedExperience(connectorProps?: any): Promise<React.ComponentClass> {
        // If the connector has not been loaded yet, then load it first
        if (!this.experienceConnector) {
            await this.loadConnector();
        }

        // Return a promise that will resolve to the connected component, ready to render.
        try {
            return this.experienceConnector.connectComponent(this.experienceComponent, connectorProps);
        } catch (error) {
            Telemetry.sendAppErrorEvent({
                ...ConnectComponentFailureError,
                message: "connectComponent failed",
                pb: {
                    ...ConnectComponentFailureError.pb,
                    customMessage: `Details: " ${JSON.stringify({
                        error,
                        connectorProps,
                        instanceId: this.instanceId,
                        parentNamespace: this.parentNamespace,
                        configIndex: this.configIndexId
                    })}`
                }
            });

            throw new Error("ExperienceLoader connectComponent failed");
        }
    }

    /**
     * update loaded milestone for the experience
     * @param experienceType - Experience Type to update
     */
    private updateExperienceLoadedMilestone(experienceType: string): void {
        const milestoneDataConnector = getMilestoneDataConnector();
        if (milestoneDataConnector) {
            MilestoneDataActions.updateExperienceCurrentState.getActionSender(milestoneDataConnector)
                .send([createExperienceStateUpdate(experienceType, ExperienceLoadingState.loaded, null, null)]);
        }
    }
}

/**
 * Creates a namespace based on the passed in experience config info
 * @param experienceConfigInfo
 */
export function getExperienceNamespace(experienceConfigInfo: ChildExperienceConfigInfo, instanceIdOverride?: string): string {
    const instanceId = instanceIdOverride || (experienceConfigInfo && experienceConfigInfo.instanceId) || "";
    return getConfigIdString(experienceConfigInfo, instanceId);
}